import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { Backfill } from "../graphql/types";
import { getCurrentUtcDate } from "../lib/Util";
import { OptionalRange } from "../types";

type Subgraph = {
  import_names: Array<string>;
  export_names: Array<string>;
  links: { [key: string]: string[] };
};

const addUpload = (subgraph: Subgraph, currentSubgraph: Subgraph, export_name: string) => {
  const new_imports = Object.keys(subgraph.links).filter((new_import) =>
    subgraph.links[new_import].includes(export_name)
  );

  const newLinks: { [key: string]: string[] } = {};

  new_imports.forEach((new_import) => {
    newLinks[new_import] = currentSubgraph.links[new_import]
      ? [...currentSubgraph.links[new_import], export_name]
      : [export_name];
  });

  const uniqueImports = currentSubgraph.import_names
    ? new Set([...currentSubgraph.import_names, ...new_imports])
    : new Set([...new_imports]);
  const uniqueExports = currentSubgraph.export_names
    ? new Set([...currentSubgraph.export_names, export_name])
    : new Set([export_name]);
  return {
    import_names: [...uniqueImports],
    export_names: [...uniqueExports],
    links: currentSubgraph.links ? { ...currentSubgraph.links, ...newLinks } : { ...newLinks },
  };
};

const removeUpload = (currentSubgraph: Subgraph, export_name: string) => {
  //impactedImports are the imports that has a link to that export
  const impactedImports = Object.keys(currentSubgraph.links).filter((_import) =>
    currentSubgraph.links[_import].includes(export_name)
  );
  const newLinks = { ...currentSubgraph.links };
  const removedImports: string[] = [];
  impactedImports.forEach((_import) => {
    //remainingUploads are those exports that doesn't have link to the import
    const remainingUploads = currentSubgraph.links[_import].filter((_export) => _export !== export_name);
    if (remainingUploads.length > 0) {
      // link that exports to the import
      newLinks[_import] = remainingUploads;
    } else {
      removedImports.push(_import);
      //if the import doesn't have any export different to that export, needs to delete the link
      delete newLinks[_import];
    }
  });

  return {
    import_names: [...currentSubgraph.import_names.filter((_import) => !removedImports.includes(_import))],
    export_names: [...currentSubgraph.export_names.filter((_export) => _export !== export_name)],
    links: { ...newLinks },
  };
};

type Steps = 0 | 1 | 2;

enum ErrorType {
  empty = "",
  conflictingBackfillsError = "ConflictingBackfillsError",
  otherError = "OtherError",
}

type BackfillProps = {
  showModal: boolean;
  step: Steps;
  showDefaultName: boolean;
  isLoading: boolean;
  uploadName: string;
  date: {
    start: Date;
    end: Date;
  };
  backfillName: string;
  backfillDescription: string;
  initialSubgraph: Subgraph;
  subgraph: Subgraph;
  currentSubgraph: Subgraph;
  errorType: ErrorType;
  conflictingBackfills: Backfill[];
  hoveredEl: string;
};

type Actions = {
  updateDescription: (description: string) => void;
  updateName: (name: string) => void;
  updateDate: (date: OptionalRange<Date>) => void;
  toggleModalOpen: () => void;
  goStepBack: (steps: Steps) => void;
  nextStep: (loading?: boolean) => void;
  updateUploadName: (uploadName: string) => void;
  updateGraph: (export_name: string) => void;
  updateGraphFilters: (exportNamesfilter: string) => void;
  setSubGraph: (payload: Subgraph) => void;
  resetCurrentSubgraph: () => void;
  resetStore: () => void;
  doneLoading: () => void;
  setErrorLaunching: (errorType: ErrorType, conflictingBackfills?: Backfill[]) => void;
  setHoveredEl: (hoveredEl: string) => void;
  setCurrentSubgraph: (payload: Subgraph) => void;
};

type BackfillState = BackfillProps & Actions;
const currentUtcDate = getCurrentUtcDate();
const initialState: BackfillProps = {
  showModal: false,
  step: 0,
  uploadName: "",
  date: {
    start: currentUtcDate,
    end: currentUtcDate,
  },
  isLoading: false,
  backfillName: "",
  backfillDescription: "",
  initialSubgraph: {
    import_names: [],
    export_names: [],
    links: {},
  },
  subgraph: {
    import_names: [],
    export_names: [],
    links: {},
  },
  currentSubgraph: { import_names: [], export_names: [], links: {} },
  errorType: ErrorType.empty,
  conflictingBackfills: [],
  showDefaultName: true,
  hoveredEl: "",
};

const useBackfillStore = create<BackfillState>()(
  immer<BackfillState>((set) => ({
    ...initialState,
    // Reset data fields to initial (keeps action methods) — used when clearing client state.
    resetStore: () =>
      set(() => ({
        ...initialState,
      })),
    updateDescription: (value) =>
      set(() => ({
        backfillDescription: value,
      })),
    updateName: (value) => {
      set(() => ({
        backfillName: value,
        showDefaultName: false,
      }));
    },
    updateDate: (value) => {
      set(() => ({
        date: value,
      }));
    },
    toggleModalOpen: () => {
      set((state) => {
        if (state.showModal) {
          return {
            ...initialState,
          };
        }
        return {
          ...initialState,
          showModal: true,
        };
      });
    },
    goStepBack: (stepsBack = 1) => {
      set((state) => ({
        step: state.step - stepsBack,
      }));
    },
    nextStep: (loading = false) => {
      set((state) => ({
        step: state.step + 1,
        isLoading: loading,
      }));
    },
    updateUploadName: (value) => {
      set((state) => ({
        uploadName: value,
        backfillName: state.showDefaultName ? value : state.backfillName,
      }));
    },
    updateGraph: (exportName) => {
      set((state) => {
        if (state.currentSubgraph.export_names.includes(exportName)) {
          state.currentSubgraph = removeUpload(state.currentSubgraph, exportName);
        } else {
          state.currentSubgraph = addUpload(state.subgraph, state.currentSubgraph, exportName);
        }
        if (state.showDefaultName) {
          state.backfillName =
            state.currentSubgraph.export_names.length > 1 ? state.uploadName.concat(" and others") : state.uploadName;
        }
      });
    },
    updateGraphFilters: (filterText) => {
      set((state) => {
        if (!filterText) {
          state.subgraph = state.initialSubgraph;
          return;
        }
        const filteredExportNames = state.initialSubgraph.export_names.filter((exportName) =>
          exportName.toLowerCase().includes(filterText.toLowerCase())
        );

        state.currentSubgraph.export_names.forEach((exportName) => {
          if (!filteredExportNames.includes(exportName)) {
            filteredExportNames.push(exportName);
          }
        });
        state.subgraph = {
          import_names: [],
          export_names: [],
          links: {},
        };

        filteredExportNames.forEach((exportName) => {
          state.subgraph = addUpload(state.initialSubgraph, state.subgraph, exportName);
        });
      });
    },
    setSubGraph: (payload) => {
      set(() => ({
        initialSubgraph: payload,
        subgraph: payload,
      }));
    },
    setCurrentSubgraph: (payload) => {
      set(() => ({
        currentSubgraph: payload,
      }));
    },
    resetCurrentSubgraph: () => {
      set(() => ({
        currentSubgraph: { ...initialState.currentSubgraph },
      }));
    },
    doneLoading: () => {
      set(() => ({
        isLoading: false,
      }));
    },
    setErrorLaunching(errorType, conflictingBackfills = []) {
      set(() => ({
        errorType: errorType,
        conflictingBackfills: conflictingBackfills,
      }));
    },
    setHoveredEl: (hoveredEl) => {
      set(() => ({
        hoveredEl: hoveredEl,
      }));
    },
  }))
);

export { Steps, Subgraph, useBackfillStore };
